import { Application, TextureGCSystem } from 'pixi.js';
import FloorplanEventService from 'floorplan-v2/app/services/floorplanEventService';
import PeriodicUpdateService from 'floorplan-v2/app/services/periodicUpdate';
import {Unsubscribe} from "@reduxjs/toolkit";
import FloorplanSpriteService from "floorplan-v2/app/services/FloorplanSpriteService";
import { APP_STATUS } from 'floorplan-v2/app/services/appStart/types';
import FloorplanNonReduxState from 'floorplan-v2/app/services/floorplanNonReduxState';
import AdminService from 'external-services/diary-admin-service/admin.service';
import { AdminServiceLog } from 'shared-types/index';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import store from 'floorplanStore';
import { CONTACT_MSG } from 'appRoot/app-error-messages';

const NS = 'AppStartService';

class AppStartService {
  private appInstance: Application;
  public appStatus = APP_STATUS.idle;
  private onDestroyUnsubscribeList: Unsubscribe[] = [];
  private rootEl: HTMLElement;
  private destroyedCB: () => void;
  private textureGC: TextureGCSystem;

  async initApp(
    rootEl: HTMLElement,
    addToWindow = false,
    width = 800,
    height = 600,
    backgroundColor = 0x5bba6f,
    destroyedCB: () => void
  ): Promise<Application> {

    this.rootEl = rootEl;
    this.destroyedCB = destroyedCB;


    let app: Application;
    try {
      app = new Application();

      await app.init({
        width,
        height,
        backgroundColor,
        antialias: true,
        autoDensity: true,
        // we did some test find that if ratio is 1, it's better set to 1.5 to make it clear in standard monitor
        resolution: window.devicePixelRatio === 1 ? 1.5 : (window.devicePixelRatio || 2),
        // resolution: window.devicePixelRatio || 1,
        eventMode: 'passive',

        /**
         * webgpu is faster, but as of Dec 2024, not stable on all devices and difficult to reliably detect.
         * PixiJS opted to default to WebGL as of v8.1.0 (https://github.com/pixijs/pixijs/releases/tag/v8.1.0)
         * We might be able to remove this 'preference' property, so WebGPU can be used where supported in the
         * near future. If we can update PixiJS to a version higher than 8.1.0 then this will happen automatically
         * when WebGPU is considered stable.
         */
        preference: 'webgl',

        eventFeatures: {
          move: true,
          /** disables the global move events which can be very expensive in large scenes */
          globalMove: false,
          click: true,
          wheel: false, // handled by pixi viewport
        }
      });

      this.textureGC = app.renderer.textureGC;
      this.textureGC.maxIdle = 60 * 10; // every 10 seconds when running at 60 FPS

      FloorplanSpriteService.setRenderer(app.renderer);

      if (addToWindow) {
        (window as any).__PIXI_APP__ = app; // for Chrome dev tool
      }

      rootEl.appendChild(app.canvas);

      this.showCanvas();
      this.clearUnsubscribeList();

      // Start the PixiJS app
      app.start();
      this.appStatus = APP_STATUS.active

    } catch (err) {
      console.warn('Error trying to start PixiJS app', err);

      this.appStatus = APP_STATUS.idle;

      const errorMessage = (err as any)?.message || err.toString();
      
      AdminService.serverLoggingDbEvents(
        AdminService.getVenue().id.toString(),
        'Floorplan failed to load',
        AdminServiceLog.ERRORLOG,
        errorMessage
      );

      store.dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Floor Plan failed to load',
          errorMessage: `There was an error while loading your Floor Plan. \n\n${CONTACT_MSG}`,
          accordionDetail: errorMessage || 'No error detail'
        })
      );
      
      return null;
    }

    this.appInstance = app;
    FloorplanEventService.setRootEl(rootEl);
    return app;
  }

  private hideCanvas() {
    try {
      if (!this.rootEl) return;

      this.rootEl.setAttribute("style", "visibility:hidden");
    } catch (error) {}
  }

  private showCanvas() {
    try {
      if (!this.rootEl) return;

      this.rootEl.setAttribute("style", "visibility:visible");
    } catch (error) {}
  }

  stopApp(): void {
    PeriodicUpdateService.setIsStopTrack(true);
    this.forceGarbageCollection();
    this.appInstance?.stop();
    this.appStatus = APP_STATUS.paused;
    this.hideCanvas();
  }

  resumeApp(): void {
    this.appInstance?.start();
    this.appStatus = APP_STATUS.active;
    this.showCanvas();
  }

  destroyApp(): void {
    this.clearUnsubscribeList();
    PeriodicUpdateService.init();
    this.destroyedCB();
    this.appStatus = APP_STATUS.idle;
    try {
      FloorplanSpriteService.clearTextures();
      this.forceGarbageCollection();
      this.appInstance?.destroy(true, true);
    } catch (e) {
      console.log('Error while destroying app', e);
    }
    (window as any).__PIXI_APP__ = undefined;
    FloorplanNonReduxState.setRerenderCallback(null);
    this.textureGC = null;
  }

  clearUnsubscribeList(): void {
    if (!this.onDestroyUnsubscribeList?.length) {
      return;
    }
    this.onDestroyUnsubscribeList.forEach(unsubscribe => unsubscribe());
    this.onDestroyUnsubscribeList = [];
  }

  addToUnsubscribeList(unsubscribe: Unsubscribe): void {
    this.onDestroyUnsubscribeList.push(unsubscribe);
  }

  getRootElement(): HTMLElement {
    return this.rootEl;
  }

  getAppStatus(): APP_STATUS {
    return this.appStatus;
  }

  forceGarbageCollection() {
    if (this.textureGC) {
      // console.log('+++++++++++++++ count', this.textureGC.checkCount)
      this.textureGC.run();
    }
  }
}

const instance = new AppStartService();
export default instance as AppStartService;
